// IDEAS — floating feedback widget, mounted once at the App root so it shows
// across every route (public Home/Designer/DTF + Admin). Submits a screenshot
// (full page, dragged area, or PC upload) plus a note. Page URL + the signed-in
// user are attached automatically.
//
// Disabled-by-admin path: GET /api/ideas/config returns { enabled }. When false,
// the widget renders nothing. Toggle lives in Admin → Settings → Platform Ideas.
//
// Ported from mymcflex's FlexIdeasWidget. Differences:
//   • TypeScript (not JSX)
//   • Gold accent (EVRST) instead of red (Flex)
//   • Base64 JSON upload via apiFetch — matches /api/files's upload style and
//     keeps us off @fastify/multipart for this one route.
import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@workos-inc/authkit-react'
import { apiFetch } from '../lib/api'

// Double-rAF: gives the panel time to unmount before html2canvas snapshots
// (without this the screenshot includes the widget itself).
const raf2 = () => new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())))

interface Rect { x: number; y: number; w: number; h: number }

export default function IdeasWidget(): JSX.Element | null {
  const { getAccessToken } = useAuth()
  const [enabled, setEnabled] = useState<boolean | null>(null)
  const [open, setOpen] = useState(false)
  const [capturing, setCapturing] = useState(false)
  const [selecting, setSelecting] = useState(false)
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState('')
  const [blob, setBlob] = useState<Blob | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    let alive = true
    fetch('/api/ideas/config').then((r) => r.json()).then((d: { enabled?: boolean }) => {
      if (alive) setEnabled(d?.enabled !== false)
    }).catch(() => { if (alive) setEnabled(false) })
    return () => { alive = false }
  }, [])

  if (!enabled) return null

  function reset() {
    setNote(''); setBlob(null)
    if (preview) URL.revokeObjectURL(preview)
    setPreview(null); setErr(null); setDone(false)
  }
  function setShot(b: Blob | null) {
    if (preview) URL.revokeObjectURL(preview)
    setBlob(b)
    setPreview(b ? URL.createObjectURL(b) : null)
  }

  async function captureFull() {
    setErr(null); setBusy(true); setOpen(false); setCapturing(true)
    try {
      await raf2()
      const html2canvas = (await import('html2canvas')).default
      const canvas = await html2canvas(document.body, {
        useCORS: true, logging: false, scale: 1, backgroundColor: '#ffffff',
      })
      canvas.toBlob((b) => { setShot(b); setCapturing(false); setOpen(true); setBusy(false) }, 'image/png')
    } catch {
      setCapturing(false); setOpen(true); setBusy(false)
      setErr('Could not capture the page automatically — try “Upload from PC”.')
    }
  }

  function startArea() { setErr(null); setOpen(false); setSelecting(true) }

  async function captureArea(rect: Rect) {
    setSelecting(false); setBusy(true); setCapturing(true)
    try {
      await raf2()
      const html2canvas = (await import('html2canvas')).default
      const canvas = await html2canvas(document.body, {
        useCORS: true, logging: false, scale: 1, backgroundColor: '#ffffff',
        x: rect.x, y: rect.y, width: rect.w, height: rect.h,
      })
      canvas.toBlob((b) => { setShot(b); setCapturing(false); setOpen(true); setBusy(false) }, 'image/png')
    } catch {
      setCapturing(false); setOpen(true); setBusy(false)
      setErr('Could not capture that area — try “Upload from PC”.')
    }
  }

  function onUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    if (f) setShot(f)
    e.target.value = ''
  }

  async function submit() {
    if (!note.trim() && !blob) { setErr('Add a note or a screenshot.'); return }
    setBusy(true); setErr(null)
    try {
      // Send as JSON with base64 screenshot — matches the /api/files upload
      // style in this app, no multipart parser needed on the route.
      let screenshot: string | undefined
      if (blob) {
        const buf = await blob.arrayBuffer()
        const bytes = new Uint8Array(buf)
        let bin = ''
        for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
        screenshot = `data:image/png;base64,${btoa(bin)}`
      }
      await apiFetch(getAccessToken, '/api/ideas', {
        method: 'POST',
        body: JSON.stringify({ note: note.trim(), pageUrl: window.location.href, screenshot }),
      })
      reset(); setDone(true)
    } catch (e: unknown) {
      const m = (e as Error)?.message ?? 'Could not send. Please try again.'
      setErr(m)
    } finally { setBusy(false) }
  }

  return (
    <div style={{ display: capturing ? 'none' : undefined }}>
      {/* Floating side tab — vertical writing, right edge of the viewport. */}
      {!open && (
        <button
          onClick={() => { setOpen(true); setDone(false) }}
          className="fixed right-0 top-1/2 -translate-y-1/2 z-[60] bg-gold hover:bg-gold-dark text-ink font-bold tracking-wide rounded-l-lg shadow-lg px-2 py-3 uppercase"
          style={{ writingMode: 'vertical-rl' }}
          aria-label="Open IDEAS feedback"
        >
          IDEAS
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed right-4 bottom-4 z-[60] w-[340px] max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-2xl border border-border overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-gold text-ink">
            <div className="font-heading uppercase font-bold tracking-wide">Ideas</div>
            <button onClick={() => setOpen(false)} className="text-ink/70 hover:text-ink" aria-label="Close">✕</button>
          </div>

          <div className="p-4">
            {done ? (
              <div className="text-center py-4">
                <div className="text-ink font-semibold">Thanks for the feedback!</div>
                <div className="text-ink-3 text-sm mt-1">Our team will take a look.</div>
                <button onClick={() => setDone(false)} className="text-ink-3 hover:text-ink text-sm mt-3 underline">Send another</button>
              </div>
            ) : (
              <>
                <p className="text-xs text-ink-3 mb-3">Spotted a bug or have an idea? Capture the screen, add a note, and send. We include this page and your login automatically.</p>

                {preview ? (
                  <div className="relative mb-3">
                    <img src={preview} alt="screenshot" className="w-full rounded border border-border max-h-40 object-contain bg-surface" />
                    <button onClick={() => setShot(null)} className="absolute top-1 right-1 bg-ink/70 text-white text-xs rounded px-1.5 py-0.5">Remove</button>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <CapBtn onClick={captureFull} disabled={busy} label="Full page" icon="🖥" />
                    <CapBtn onClick={startArea}  disabled={busy} label="Select area" icon="⬚" />
                    <CapBtn onClick={() => fileRef.current?.click()} disabled={busy} label="Upload" icon="⬆" />
                  </div>
                )}
                <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" />

                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  placeholder="Add your feedback…"
                  className="input text-sm"
                />

                {err && <p className="text-red-700 text-sm mt-2">{err}</p>}

                <button
                  onClick={submit}
                  disabled={busy}
                  className="w-full mt-3 inline-flex items-center justify-center px-4 py-2.5 rounded bg-gold text-ink font-semibold hover:bg-gold-dark disabled:opacity-60"
                >
                  {busy ? 'Working…' : 'Send feedback'}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Drag-to-select overlay — only mounted while the user is choosing an area. */}
      {selecting && <AreaSelector onCancel={() => { setSelecting(false); setOpen(true) }} onSelect={captureArea} />}
    </div>
  )
}

function CapBtn({ onClick, disabled, label, icon }: { onClick: () => void; disabled?: boolean; label: string; icon: string }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex flex-col items-center gap-1 px-1 py-2 rounded border border-border text-ink-2 text-[11px] font-medium hover:bg-surface disabled:opacity-50"
    >
      <span className="text-base" aria-hidden>{icon}</span>{label}
    </button>
  )
}

// Click-and-drag rectangle to capture a region. Coordinates are translated to
// document space so html2canvas crops correctly regardless of scroll position.
function AreaSelector({ onCancel, onSelect }: { onCancel: () => void; onSelect: (r: Rect) => void }) {
  const [start, setStart] = useState<{ x: number; y: number } | null>(null)
  const [cur, setCur] = useState<{ x: number; y: number } | null>(null)

  const down = (e: React.MouseEvent) => { setStart({ x: e.clientX, y: e.clientY }); setCur({ x: e.clientX, y: e.clientY }) }
  const move = (e: React.MouseEvent) => { if (start) setCur({ x: e.clientX, y: e.clientY }) }
  const up = () => {
    if (!start || !cur) { onCancel(); return }
    const x = Math.min(start.x, cur.x), y = Math.min(start.y, cur.y)
    const w = Math.abs(cur.x - start.x), h = Math.abs(cur.y - start.y)
    if (w < 8 || h < 8) { onCancel(); return }
    onSelect({ x: x + window.scrollX, y: y + window.scrollY, w, h })
  }

  const box = start && cur ? {
    left: Math.min(start.x, cur.x), top: Math.min(start.y, cur.y),
    width: Math.abs(cur.x - start.x), height: Math.abs(cur.y - start.y),
  } : null

  return (
    <div
      className="fixed inset-0 z-[70] cursor-crosshair"
      style={{ background: 'rgba(15,17,23,0.25)' }}
      onMouseDown={down}
      onMouseMove={move}
      onMouseUp={up}
    >
      <div className="fixed top-4 left-1/2 -translate-x-1/2 bg-ink text-white text-sm px-3 py-1.5 rounded-full shadow">
        Drag to select an area · <button onClick={(e) => { e.stopPropagation(); onCancel() }} className="underline">cancel</button>
      </div>
      {box && (
        <div className="fixed border-2 border-gold bg-gold/10" style={{ left: box.left, top: box.top, width: box.width, height: box.height }} />
      )}
    </div>
  )
}
